import { useCallback } from 'react';
import type { Annotation } from '../types';

/** Params for annotation canvas operations */
export interface UseAnnotationCanvasOperationsParams {
  history: Annotation[][];
  setHistory: (v: Annotation[][] | ((prev: Annotation[][]) => Annotation[][])) => void;
  historyIndex: number;
  setHistoryIndex: (v: number | ((prev: number) => number)) => void;
  annotationsRef: React.MutableRefObject<Annotation[]>;
  setAnnotations: (v: Annotation[] | ((prev: Annotation[]) => Annotation[])) => void;
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  pageNumber: number;
  setSelectedAnnotation: (id: string | null) => void;
  setSelectedAnnotations: (v: Set<string> | ((prev: Set<string>) => Set<string>)) => void;
  persistCreatedAnnotations: (newItems: Annotation[]) => Promise<{ next: Annotation[]; changedComponentTypes: string[] }>;
  promptedPagesRef: React.MutableRefObject<Set<number>>;
  supabase: any;
  estimationId: string;
  activeMode?: 'draw' | 'specs' | 'rfi' | 'neca' | 'estimate';
  currentDrawingFileId?: string;
  clearPersistedAnnotations: () => Promise<void>;
}

export function useAnnotationCanvasOperations(params: UseAnnotationCanvasOperationsParams) {
  const {
    history,
    setHistory,
    historyIndex,
    setHistoryIndex,
    annotationsRef,
    setAnnotations,
    canvasRef,
    pageNumber,
    setSelectedAnnotation,
    setSelectedAnnotations,
    persistCreatedAnnotations,
    promptedPagesRef,
    supabase,
    estimationId,
    activeMode,
    currentDrawingFileId,
    clearPersistedAnnotations,
  } = params;

  const addToHistory = useCallback(
    (newAnnotations: Annotation[]) => {
      const newHistory = history.slice(0, historyIndex + 1);
      newHistory.push(newAnnotations);
      setHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
    },
    [history, historyIndex, setHistory, setHistoryIndex]
  );

  const renumberAnnotations = useCallback((annotationList: Annotation[]): Annotation[] => {
    const grouped = new Map<string, Map<number, Annotation[]>>();
    for (const annotation of annotationList) {
      const componentType = annotation.component_type || 'unknown';
      const page = annotation.page;
      if (!grouped.has(componentType)) {
        grouped.set(componentType, new Map<number, Annotation[]>());
      }
      if (!grouped.get(componentType)!.has(page)) {
        grouped.get(componentType)!.set(page, []);
      }
      grouped.get(componentType)!.get(page)!.push(annotation);
    }
    const renumbered: Annotation[] = [];
    const groupedArray = Array.from(grouped.entries());
    for (let i = 0; i < groupedArray.length; i++) {
      const [componentType, pages] = groupedArray[i];
      let componentCounter = 1;
      const sortedPages = Array.from(pages.keys()).sort((a: number, b: number) => a - b);
      for (const page of sortedPages) {
        const pageAnnotations = pages.get(page)!;
        pageAnnotations.sort((a: Annotation, b: Annotation) => {
          const yDiff = (a.startY || 0) - (b.startY || 0);
          if (Math.abs(yDiff) > 20) return yDiff;
          return (a.startX || 0) - (b.startX || 0);
        });
        for (const annotation of pageAnnotations) {
          annotation.number = componentCounter++;
          if (annotation.text) {
            annotation.text = `${componentType} #${annotation.number}`;
          }
          renumbered.push(annotation);
        }
      }
    }
    return renumbered;
  }, []);

  const handleCanvasDrop = useCallback(
    (e: React.DragEvent<HTMLCanvasElement>) => {
      e.preventDefault();

      const canvas = canvasRef.current;
      if (!canvas) return;

      // Get component data from drag
      const componentData = e.dataTransfer.getData('component');
      if (!componentData) return;

      try {
        const component = JSON.parse(componentData);

        // Calculate drop position
        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        const x = (e.clientX - rect.left) * scaleX;
        const y = (e.clientY - rect.top) * scaleY;

        // For lighting fixtures, use symbol as component_type (unique per fixture)
        const dropComponentType = component.symbolType === 'Lighting'
          ? (component.symbol || component.name)
          : component.name;

        const baseAnnotations = annotationsRef.current;

        // Get count for this component type
        const existingCount = baseAnnotations.filter(
          a => a.component_type === dropComponentType && a.page === pageNumber
        ).length;

        // Create new rectangle annotation (default 60x60 size, centered at drop point)
        const defaultSize = 60;
        const newAnnotation: Annotation = {
          id: `manual-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          type: 'rectangle',
          startX: x - defaultSize / 2,
          startY: y - defaultSize / 2,
          width: defaultSize,
          height: defaultSize,
          color: component.color,
          thickness: 3,
          timestamp: new Date().toISOString(),
          page: pageNumber,
          component_type: dropComponentType,
          number: existingCount + 1
        };

        // Persist through create commit path and then snapshot history.
        void persistCreatedAnnotations([newAnnotation])
          .then(({ next }) => {
            addToHistory(next);
            setSelectedAnnotation(newAnnotation.id);
          })
          .catch((persistError) => {
            console.error('Error persisting dropped annotation:', persistError);
          });

        console.log(`📍 Dropped ${component.name} at (${Math.round(x)}, ${Math.round(y)})`);
      } catch (error) {
        console.error('Error handling drop:', error);
      }
    },
    [
      canvasRef,
      annotationsRef,
      pageNumber,
      setAnnotations,
      addToHistory,
      setSelectedAnnotation,
      persistCreatedAnnotations,
    ]
  );

  const undo = useCallback(() => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setAnnotations(history[newIndex]);
    }
  }, [historyIndex, history, setHistoryIndex, setAnnotations]);

  const redo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      setAnnotations(history[newIndex]);
    }
  }, [historyIndex, history, setHistoryIndex, setAnnotations]);

  const clearAll = useCallback(async () => {
    const deleteStack = new Error('clearAll invocation stack');
    console.error('🧭 [ANNOTATION DELETE] clearAll requested', {
      estimationId,
      activeMode,
      currentDrawingFileId: currentDrawingFileId || null,
      pageNumber,
    });
    if (deleteStack.stack) {
      console.error(deleteStack.stack);
    }

    if (typeof window !== 'undefined') {
      const confirmation = window.prompt(
        'Type DELETE ALL to permanently clear annotations for the current scope.'
      );
      if (confirmation !== 'DELETE ALL') {
        console.warn('⏭️ clearAll cancelled by user');
        return;
      }
    }

    setAnnotations([]);
    annotationsRef.current = [];
    addToHistory([]);
    setSelectedAnnotation(null);
    setSelectedAnnotations(new Set());

    // Clear the prompted pages cache so user can get prompt again
    promptedPagesRef.current.clear();
    console.log('🔄 Cleared auto-annotation prompt cache');

    // Also clear auto-annotation prompt history from database
    if (supabase && estimationId) {
      try {
        // Delete messages that contain auto-annotation prompt history
        const { error } = await supabase
          .from('estimation_chat_messages')
          .delete()
          .eq('estimation_id', estimationId)
          .eq('agent', 'System')
          .or('content.ilike.%Auto-annotation%');

        if (error) {
          console.error('Error clearing auto-annotation history:', error);
        } else {
          console.log('✅ Cleared auto-annotation prompt history from database');
        }
      } catch (error) {
        console.error('Error clearing prompt history:', error);
      }
    }

    if (!estimationId) return;

    console.log('🗑️ Clearing annotations from database (scoped)...');
    try {
      await clearPersistedAnnotations();
    } catch (err) {
      console.error('Failed to clear annotations:', err);
    }
  }, [
    addToHistory,
    setAnnotations,
    annotationsRef,
    setSelectedAnnotation,
    setSelectedAnnotations,
    promptedPagesRef,
    supabase,
    estimationId,
    activeMode,
    currentDrawingFileId,
    clearPersistedAnnotations,
    pageNumber,
  ]);

  return {
    addToHistory,
    renumberAnnotations,
    handleCanvasDrop,
    undo,
    redo,
    clearAll,
  };
}
